<template>
  <div class="dialog-step">
    <div v-if="isDrawer" class="header-sticky">
      <div class="header">
        <span class="title">{{
          isSuccess
            ? $t('common.transactionSubmitted')
            : $t('common.transactionRejected')
        }}</span>
        <PButton class="close-button" icon="pi pi-times" @click="onComplete" />
      </div>
      <hr class="divider" />
    </div>
    <div v-if="!isDrawer" class="dialog-step__header">
      <!-- TODO: temporarily remove the extra close button. Most likely it will not be needed -->
      <!-- <p-button
        class="dialog-step__button ml-auto"
        icon="pi pi-times"
        @click="onComplete"
      /> -->
    </div>
    <div class="dialog-step__content">
      <div :style="{ margin: isDrawer ? '0px 15%' : '0px' }" class="submit">
        <img
          :src="image"
          :style="{
            width: isDrawer ? '300px' : '60px',
            height: isDrawer ? '300px' : '60px',
            margin: isDrawer ? '-2.5rem 0px' : '0rem',
          }"
        />
        <h3
          :style="{ marginTop: isDrawer ? '0px' : '24px' }"
          class="submit__title"
        >
          {{
            isSuccess
              ? $t('common.transactionSubmitted')
              : $t('errors.somethingWentWrong')
          }}
        </h3>
        <slot
          name="description"
          v-bind="{ description, isSuccess, isInternalError, txState }"
        >
          <p v-if="description" class="submit__description">
            {{ description }}
          </p>
        </slot>
        <p v-if="isInternalError" class="submit__error">{{ txMessage }}</p>
        <div v-else class="tx-result mt-4">
          <div class="tx-result__row">
            <div class="tx-result__field">
              <div class="tx-result__label">
                {{ $t('common.transactionHash') }}
              </div>
              <div class="tx-result__value">
                <div :title="txState.hash" class="tx-hash">
                  <span>{{ shortAddress(txState.hash, 10, 10) }}</span>
                  <PButton
                    v-tooltip="$t('common.clickToCopy')"
                    class="p-button-sm p-button-rounded p-button-text"
                    icon="pi pi-copy"
                    @click="copyAddress"
                  />
                </div>
              </div>
              <a
                :href="txLink"
                class="tx-result__link"
                rel="noreferrer noopener"
                target="_blank"
              >
                {{ $t('common.viewInExplorer') }}
                <img
                  alt="external link"
                  class="tx-result__img"
                  src="@/assets/external-link.svg"
                />
              </a>
            </div>
          </div>
          <div class="tx-result__row">
            <div class="tx-result__field" style="flex: 1">
              <div class="tx-result__label">{{ $t('common.gasUsed') }}</div>
              <div class="tx-result__value">{{ txState.gas_used }}</div>
            </div>
            <div class="tx-result__field">
              <div class="tx-result__label">{{ $t('common.status') }}</div>
              <div class="tx-result__value">
                <span :class="{ submit__error: !isSuccess }">
                  {{ isSuccess ? 'Success' : 'Failed' }}
                </span>
              </div>
            </div>
          </div>
          <div class="tx-result__row">
            <div class="tx-result__field">
              <div class="tx-result__label">{{ $t('common.message') }}</div>
              <div :class="{ 'text-sm': !isSuccess }" class="tx-result__value">
                <template v-for="(item, index) in txMessage" :key="index">
                  <component :is="item.tag" :class="item.class">{{
                    item.text
                  }}</component>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      :class="{
        'footer-sticky': isDrawer,
        'dialog-step__footer mt-4': !isDrawer,
      }"
    >
      <p-button
        class="p-button-primary justify-content-center w-full"
        @click="onComplete"
      >
        {{ isSuccess ? 'Close' : 'Dismiss' }}
      </p-button>
    </div>
  </div>
  <CopyNotification ref="dialog" />
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { shortAddress } from '@/utils';
import { useClipboard } from '@vueuse/core';

import { CopyNotification } from '@/components/UI/CopyNotification';
import { useWalletPlugin } from '@/composables/useWalletPlugin';
import successSvg from '@/assets/success.svg';
import success2Svg from '@/assets/success-2.svg';
import rejectedSvg from '@/assets/rejected.svg';
import rejected2Svg from '@/assets/rejected-2.svg';
import {
  ChainsEnum,
  CORRECT_CHAIN,
  EXPLORER_APTOS,
  EXPLORER_MOVEMENT,
  EXPLORER_URL,
} from '@/constants';

type State =
  | {
      status: 'success';
      tx: any;
    }
  | {
      tx?: any;
      status: 'failed';
      message: string;
    };

interface IProps {
  state: State;
  description?: string;
  isDrawer?: boolean;
}

const dialog = ref();
const props = defineProps<IProps>();
const emits = defineEmits(['close', 'success', 'reject']);

const image = computed(() => {
  if (isSuccess.value) {
    if (!props.isDrawer) return successSvg;
    else return success2Svg;
  } else {
    if (!props.isDrawer) return rejectedSvg;
    else return rejected2Svg;
  }
});

const { network: walletNetwork } = useWalletPlugin();

const txState = computed(() => props.state.tx);
const isInternalError = computed(() => !isSuccess.value && !txState.value);
const isSuccess = computed(() => props.state.status === 'success');
const network = computed(() => {
  const networkName = String(walletNetwork.value?.name).toLowerCase();
  if (isMovement.value) {
    return 'mainnet';
  }
  return networkName.indexOf('mainnet') >= 0
    ? 'mainnet'
    : networkName.indexOf('testnet') >= 0
    ? 'testnet'
    : 'devnet';
});

const isL2 = computed(() => CORRECT_CHAIN.id === ChainsEnum['lumio-testnet']);
const isMovement = computed(() =>
  [ChainsEnum['movement-testnet'], ChainsEnum['movement-mainnet']].includes(
    CORRECT_CHAIN.id,
  ),
);

const { copy: onCopyAddress } = useClipboard();
const txLink = computed(() =>
  isL2.value
    ? `${EXPLORER_URL}tx/${txState.value.hash}`
    : `${isMovement.value ? EXPLORER_MOVEMENT : EXPLORER_APTOS}txn/${
        txState.value.hash
      }?network=${network.value}`,
);

const description = computed(() => {
  return props.description;
});

const txMessage = computed(() => {
  if (isInternalError.value) {
    return (props.state as any)?.message;
  }

  const message = (txState.value as any)?.vm_status.split('\n');

  return message.map((item: string) => {
    return {
      tag: 'div',
      class: '-limited',
      text: item,
    };
  });
});

function copyAddress() {
  onCopyAddress(txLink.value);
  dialog.value.show();
}

function onComplete() {
  if (isSuccess.value) {
    emits('success');
  } else {
    emits('reject');
  }
}
</script>

<style lang="scss" scoped>
.dialog-step {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.dialog-step__content {
  display: flex;
  flex-grow: 1;
  justify-content: center;
}

.header-sticky {
  position: sticky;
  top: 0px;
  background-color: #16162d;
  z-index: 20;
}
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 25px;
  padding: 2rem;
  padding-bottom: 0px;
  .title {
    font-weight: 600;
    font-size: 28px;
    line-height: 34px;
    color: #e5e4fa;
  }

  & > div:last-child {
    margin-left: auto;
  }
  .close-button {
    background-color: #24243a;
    padding: 1rem;
    border-radius: 13px;
    width: 48px;
    height: 48px;
    border-width: 0px;
    &:hover {
      color: white;
    }
  }
}

.divider {
  border: 0px;
  border-top: 2px solid #fbfaff16;
  border-width: 2px 0px 0px 0px;
  margin: 0px -2rem;
}

.footer-sticky {
  width: 100%;
  background-color: #16162d;
  padding: 2rem;
}
.submit {
  display: flex;
  flex-direction: column;
  align-items: center;

  &__title {
    font-size: 20px;
    margin-bottom: 0px;
    color: var(--text-color);
    font-weight: 800;
  }

  &__description {
    color: var(--text-color);
    opacity: 0.7;
    font-size: 17px;
    margin-bottom: 0;
    margin-top: 10px;
  }

  &__error {
    color: #f03f77;
    text-align: center;
    line-height: 1.5rem;
    width: 80%;
    &::first-letter {
      text-transform: uppercase;
    }
  }
}

.tx-hash {
  width: 100%;

  .p-button {
    width: 20px !important;
    height: 20px !important;
    padding: 10px;
    margin-left: auto;
  }
}

.tx-result {
  width: 100%;
  border-radius: 16px;
  border: 1px solid transparentize(#e5e4fa, 0.85);

  margin-top: 10px;

  .tx-result__row {
    display: flex;
  }

  .tx-result__row:not(:last-child) {
    border-bottom: 1px solid transparentize(#e5e4fa, 0.85);
  }

  .tx-result__field {
    padding: 16px 16px 0;
    width: 100%;
  }

  .tx-result__label {
    white-space: nowrap;
    font-size: 13px;
    color: transparentize(#e5e4fa, 0.5);
    font-weight: 500;
    margin-bottom: 8px;
  }

  .tx-result__value {
    font-size: 17px;
    padding-bottom: 16px;
    color: #e5e4fa;
    overflow: auto;
    text-transform: capitalize;
  }

  &__link {
    color: #6e42ca;
    font-size: 13px;
    font-weight: 500;
    line-height: 15px;
    letter-spacing: 0;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding-bottom: 16px;

    .tx-result__img {
      filter: brightness(0) saturate(100%) invert(32%) sepia(16%)
        saturate(5791%) hue-rotate(236deg) brightness(93%) contrast(100%);
      margin-left: 8px;
    }
  }
}

.-limited {
  word-wrap: break-word;
  max-width: 300px;
}
</style>
